import { back, type Screen } from '@/app'
import { getPerson, type PersonDetail } from '@/lib/catalog'
import { clear, el, setText } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { IMAGE_WIDTH, mediaUrl, queueImage } from '@/lib/images'
import { openWebDetail } from '@/browser/detailModal'
import { webButton, webCard, webDescription, webEmpty, webGrid, webPage, webScreen, webTitle } from '@/browser/ui'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ŞƏXS EKRANI — BRAUZER VARİANTI (2026-08-16)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠ NİYƏ ƏLAVƏ OLUNDU (TestingClaude veb TV 39). ÖLÇÜLDÜ: `browser/main.ts`
 *   `createScreen()` marşrut cədvəlində home · live · search · browse · upcoming ·
 *   list · profile · profiles · detail · qr · player VAR idi, `person` isə YOX —
 *   yəni `Route` tipində mövcud olan marşrut brauzer hədəfində `fallbackScreen()`-ə
 *   düşürdü (boş ekran). Cihaz (Samsung/LG) hədəfində ekran 2026-08-13-dən var
 *   (`screens/person.ts`), yəni «Android TV app-ın eynisi» tələbi brauzer hədəfində
 *   yarımçıq qalmışdı: axtarışdan şəxsə keçmək mümkün deyildi.
 *
 * ⚠ CİHAZ EKRANININ SURƏTİ DEYİL, QARŞILIĞIDIR: orada maket sabit 1920×1080
 *   kətanda `FocusGrid` arifmetikası ilə qurulur; burada doğma sənəd axını,
 *   doğma `:focus` və doğma sürüşmə var (`lib/platform.ts` bölgüsü). Məlumat
 *   qatı (`getPerson`) İSƏ EYNİDİR — iki ekran heç vaxt fərqli məlumat göstərmir.
 * ⚠ KART KLİKİ MODAL AÇIR (`Docs/KINOBOX_DETAIL_MODAL.md` §1) — şəxs ekranı arxada
 *   qalır, filmoqrafiya yenidən çəkilmir. Kataloq və axtarış ekranındakı davranış.
 * ⚠ BİOQRAFİYA KƏSİLMİR, «Daha çox» ilə AÇILIR (`webDescription`): brauzerdə
 *   səhifə şaquli sürüşür, yəni cihazdakı 320 simvol həddinə ehtiyac yoxdur.
 */

export function createWebPersonScreen(slug: string): Screen {
  const page = webPage()

  // ── Başlıq bloku: dairəvi foto + ad + doğum tarixi + bioqrafiya ──────────
  const head = el('div', 'web-phead')
  const photo = el('div', 'web-phead__photo')
  const photoImg = document.createElement('img')
  photoImg.className = 'web-phead__img'
  photoImg.alt = ''
  photo.appendChild(photoImg)
  head.appendChild(photo)

  const headText = el('div', 'web-phead__text')
  const name = webTitle(T('Yüklənir…'))
  const birth = el('p', 'web-phead__birth')
  birth.hidden = true
  const bio = el('div')
  headText.appendChild(name)
  headText.appendChild(birth)
  headText.appendChild(bio)
  head.appendChild(headText)
  page.appendChild(head)

  const section = el('h2', 'web-row__title', T('Filmoqrafiya'))
  section.hidden = true
  page.appendChild(section)

  const holder = el('div')
  page.appendChild(holder)

  let alive = true

  function load(): void {
    clear(holder)
    holder.appendChild(webEmpty(T('Yüklənir…')))

    void getPerson(slug).then((person) => {
      if (!alive) return
      clear(holder)
      if (!person) {
        // «Qara ekran yoxdur» (Frontend/CLAUDE.md §6): səbəb + təkrar cəhd düyməsi.
        holder.appendChild(webEmpty(T('Bu məzmun açıla bilmədi')))
        holder.appendChild(webButton(T('Yenidən cəhd et'), load))
        holder.appendChild(webButton(T('Geri'), back, true))
        return
      }
      applyPerson(person)
    })
  }

  function applyPerson(person: PersonDetail): void {
    setText(name, person.name || person.slug)

    if (person.birthDate) {
      setText(birth, person.birthDate)
      birth.hidden = false
    }

    clear(bio)
    if (person.biography) bio.appendChild(webDescription(person.biography, 'web-phead__bio'))

    const url = mediaUrl(person.photoUrl, IMAGE_WIDTH.avatar)
    if (url) queueImage(photoImg, url)

    section.hidden = false
    if (!person.filmography.length) {
      section.hidden = true
      holder.appendChild(webEmpty(T('Bu şəxsə bağlı dərc olunmuş kontent yoxdur.')))
      return
    }
    holder.appendChild(webGrid(person.filmography.map((card) => webCard(card, () => openWebDetail(card)))))
  }

  load()

  // 2026-10-23: «Geri» üçün saxlanılır — izah `browser/ui.ts` → `webScreen` (keep-alive).
  return webScreen(page, () => {
    alive = false
  }, {})
}
